/**
 * The pure rules behind a skill's page (ADR-216 Amendment 1), kept out of the
 * components so each is tested directly: how a file is shown, where a link
 * between files goes, what changed between two versions, and how an edit is
 * carried onto a version someone published while it was being made.
 */

export interface SkillFileText {
  path: string;
  content: string;
}

/** The entry file: never renamed or deleted in the editor. */
export const ENTRY_FILE = 'SKILL.md';

/** highlight.js language by extension. Never guessed from content. */
const LANGUAGES: Record<string, string> = {
  sh: 'bash',
  bash: 'bash',
  ps1: 'powershell',
  py: 'python',
  js: 'javascript',
  mjs: 'javascript',
  ts: 'typescript',
  json: 'json',
  yaml: 'yaml',
  yml: 'yaml',
  toml: 'ini',
};

function extension(path: string): string {
  const name = path.split('/').pop() ?? '';
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

export function isMarkdown(path: string): boolean {
  return extension(path) === 'md';
}

/** The highlight.js language for a file, or undefined to show it plain. */
export function languageFor(path: string): string | undefined {
  return LANGUAGES[extension(path)];
}

/** Lines of a text, without the empty one a trailing newline leaves. */
export function linesOf(content: string): string[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

/**
 * The file a relative link in `from` points at, or undefined when it names no
 * file of the skill. `MarkdownRenderer` drops a leading `./` or `../` from a
 * link, so a link that does not resolve beside `from` is also tried from the
 * skill's root before giving up.
 */
export function resolveLink(from: string, href: string, paths: readonly string[]): string | undefined {
  const target = href.split('#')[0];
  if (!target || /^[a-z][a-z0-9+.-]*:/i.test(target) || target.startsWith('/')) return undefined;
  const folder = from.split('/').slice(0, -1);
  for (const base of [folder, []]) {
    const parts = [...base];
    for (const segment of target.split('/')) {
      if (segment === '' || segment === '.') continue;
      if (segment === '..') parts.pop();
      else parts.push(segment);
    }
    const path = parts.join('/');
    if (paths.includes(path)) return path;
  }
  return undefined;
}

export interface FileChange {
  path: string;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
}

/**
 * What changed from `from` to `to`, by content. Sorted with the changes
 * first, SKILL.md leading each group, so a review starts where it matters.
 */
export function compareFiles(from: readonly SkillFileText[], to: readonly SkillFileText[]): FileChange[] {
  const before = new Map(from.map((f) => [f.path, f.content]));
  const after = new Map(to.map((f) => [f.path, f.content]));
  const changes: FileChange[] = [];
  for (const [path, content] of after) {
    if (!before.has(path)) changes.push({ path, status: 'added' });
    else changes.push({ path, status: before.get(path) === content ? 'unchanged' : 'changed' });
  }
  for (const path of before.keys()) if (!after.has(path)) changes.push({ path, status: 'removed' });
  const rank = { changed: 0, added: 1, removed: 2, unchanged: 3 } as const;
  return changes.sort(
    (a, b) =>
      rank[a.status] - rank[b.status] ||
      Number(b.path === ENTRY_FILE) - Number(a.path === ENTRY_FILE) ||
      a.path.localeCompare(b.path),
  );
}

/** The change note an edit starts with: the files it touches. */
export function defaultNote(changes: readonly FileChange[]): string {
  const verb = { added: 'Add', removed: 'Remove', changed: 'Edit', unchanged: '' } as const;
  const touched = changes.filter((c) => c.status !== 'unchanged');
  if (touched.length === 0) return '';
  const parts = touched.slice(0, 3).map((c) => `${verb[c.status]} ${c.path}`);
  if (touched.length > 3) parts.push(`${touched.length - 3} more`);
  return parts.join(', ').slice(0, 500);
}

export interface CarriedDraft {
  files: SkillFileText[];
  /** Files both the draft and the newer version changed: the member settles each. */
  conflicts: string[];
}

/**
 * Carry a draft made from `base` onto `latest`, published meanwhile. File by
 * file: a file only one side changed takes that side; a file both changed the
 * same way is taken; a file both changed differently keeps the draft's text
 * and is flagged. Nothing is merged line by line (ADR-216 Am.1, alternatives).
 */
export function carryDraft(
  base: readonly SkillFileText[],
  latest: readonly SkillFileText[],
  draft: readonly SkillFileText[],
): CarriedDraft {
  const b = new Map(base.map((f) => [f.path, f.content]));
  const t = new Map(latest.map((f) => [f.path, f.content]));
  const m = new Map(draft.map((f) => [f.path, f.content]));
  const files: SkillFileText[] = [];
  const conflicts: string[] = [];
  const paths = [...new Set([...t.keys(), ...m.keys(), ...b.keys()])].sort();
  for (const path of paths) {
    const [was, theirs, mine] = [b.get(path), t.get(path), m.get(path)];
    let kept: string | undefined;
    if (mine === was) kept = theirs;
    else if (theirs === was || theirs === mine) kept = mine;
    else {
      conflicts.push(path);
      kept = mine ?? theirs;
    }
    if (kept !== undefined) files.push({ path, content: kept });
  }
  return { files, conflicts };
}

/** A path the editor may give a file: the server's rule (FilePath in openapi.yaml). */
export function isValidSkillPath(path: string): boolean {
  return (
    path.length > 0 &&
    path.length <= 512 &&
    /^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/.test(path) &&
    !path.split('/').some((s) => s === '.' || s === '..')
  );
}

/** One line of a diff, numbered on the side(s) it exists on; or a fold of unchanged lines. */
export type DiffRow =
  | { kind: 'same' | 'add' | 'del'; text: string; oldNo?: number; newNo?: number }
  | { kind: 'fold'; count: number };

/** A line diff's parts (jsdiff's diffLines) as numbered rows. */
export function diffRows(parts: readonly { value: string; added?: boolean; removed?: boolean }[]): DiffRow[] {
  const rows: DiffRow[] = [];
  let oldNo = 1;
  let newNo = 1;
  for (const part of parts) {
    for (const text of linesOf(part.value)) {
      if (part.added) rows.push({ kind: 'add', text, newNo: newNo++ });
      else if (part.removed) rows.push({ kind: 'del', text, oldNo: oldNo++ });
      else rows.push({ kind: 'same', text, oldNo: oldNo++, newNo: newNo++ });
    }
  }
  return rows;
}

/** Keep `context` unchanged lines around each change; fold longer unchanged runs. */
export function foldRows(rows: readonly DiffRow[], context = 3): DiffRow[] {
  const near = rows.map((_, i) =>
    rows.slice(Math.max(0, i - context), i + context + 1).some((r) => r.kind === 'add' || r.kind === 'del'),
  );
  const out: DiffRow[] = [];
  let folded = 0;
  rows.forEach((row, i) => {
    if (row.kind === 'same' && !near[i]) {
      folded++;
      return;
    }
    if (folded) out.push({ kind: 'fold', count: folded });
    folded = 0;
    out.push(row);
  });
  if (folded) out.push({ kind: 'fold', count: folded });
  return out;
}
